/**
 * Página de Reportes
 * - Unión: Vista agregada por asociación (sin finanzas)
 * - Club Director: Reportes detallados de su club
 */

import { useState, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  InlineLoader,
} from "@/components/ui";
import {
  BarChart3,
  Users,
  DollarSign,
  Award,
  ClipboardCheck,
  Download,
  TrendingUp,
  PieChart,
  Building2,
} from "lucide-react";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useMembersByClub, useScopedMembers } from "@/hooks/usePersons";
import { useActivePeriod } from "@/hooks/useCatalogs";
import {
  useChargesByClubAndPeriod,
  useFinancialSummary,
  usePaymentsByClub,
  useExpensesByClub,
} from "@/hooks/useFinance";
import { useMeetingsByClubAndPeriod } from "@/hooks/useAttendance";
import {
  useAchievementsByClub,
  useClassProgressByClub,
} from "@/hooks/useProgress";
import { useAssociations } from "@/hooks/useOrganization";
import { formatCurrency } from "@/lib/utils";
import { exportToXlsx } from "@/lib/utils/exportXlsx";
import { Can } from "@/components/auth/Can";

type ReportType =
  | "overview"
  | "members"
  | "finance"
  | "attendance"
  | "progress";

// ============================================
// COMPONENTE PRINCIPAL - Router por rol
// ============================================
export default function ReportsPage() {
  const { isUnionAdmin, isAssociationAdmin } = usePermissions();

  // Unión: Vista agregada sin finanzas
  if (isUnionAdmin) {
    return <UnionReportsView />;
  }

  // Asociación: Vista filtrada al scope (clubes propios)
  if (isAssociationAdmin) {
    return <AssociationReportsView />;
  }

  // Club Director: Vista detallada
  return <ClubReportsView />;
}

// ============================================
// VISTA DE REPORTES PARA UNIÓN (Estadísticas agregadas)
// ============================================
function UnionReportsView() {
  const { data: members, isLoading: loadingMembers } = useScopedMembers();
  const { data: associations } = useAssociations();
  const { data: clubs } = useAccessibleClubs();
  const [selectedAssociationId, setSelectedAssociationId] =
    useState<string>("all");

  // Estadísticas generales
  const stats = useMemo(() => {
    if (!members || !clubs || !associations) {
      return {
        totalMembers: 0,
        activeMembers: 0,
        totalClubs: 0,
        totalAssociations: 0,
        activeRate: 0,
      };
    }

    const active = members.filter((m) => m.status === "active").length;

    return {
      totalMembers: members.length,
      activeMembers: active,
      totalClubs: clubs.length,
      totalAssociations: associations.length,
      activeRate:
        members.length > 0 ? Math.round((active / members.length) * 100) : 0,
    };
  }, [members, clubs, associations]);

  // Estadísticas por asociación
  const statsByAssociation = useMemo(() => {
    if (!members || !clubs || !associations) return [];

    return associations
      .map((assoc) => {
        const assocClubs = clubs.filter((c) => c.associationId === assoc.id);
        const assocMembers = members.filter(
          (m) => m.associationId === assoc.id,
        );
        const activeMembers = assocMembers.filter((m) => m.status === "active");

        return {
          id: assoc.id,
          name: assoc.name,
          region: assoc.region,
          clubCount: assocClubs.length,
          totalMembers: assocMembers.length,
          activeMembers: activeMembers.length,
          activeRate:
            assocMembers.length > 0
              ? Math.round((activeMembers.length / assocMembers.length) * 100)
              : 0,
        };
      })
      .sort((a, b) => b.totalMembers - a.totalMembers);
  }, [members, clubs, associations]);

  // Filtrar por asociación seleccionada
  const filteredStats =
    selectedAssociationId === "all"
      ? statsByAssociation
      : statsByAssociation.filter((a) => a.id === selectedAssociationId);

  if (loadingMembers) {
    return (
      <div className="flex items-center justify-center h-64">
        <InlineLoader size="lg" label="Cargando reportes…" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Moderno */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            Reportes de la Unión
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            Estadísticas agregadas por asociación
          </p>
        </div>

        {/* Filtro de Asociación */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Filtrar:</label>
          <select
            value={selectedAssociationId}
            onChange={(e) => setSelectedAssociationId(e.target.value)}
            className="px-4 py-2.5 border-2 border-gray-200 rounded-xl bg-white focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 text-sm transition-all duration-200 hover:border-gray-300"
          >
            <option value="all">Todas las asociaciones</option>
            {associations?.map((assoc) => (
              <option key={assoc.id} value={assoc.id}>
                {assoc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Cards Generales - Modernos */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-white to-blue-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Total Miembros
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.totalMembers}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-white to-green-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Miembros Activos
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.activeMembers}
                </p>
                <p className="text-xs text-green-600 font-medium mt-1">
                  {stats.activeRate}% del total
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-white to-purple-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Asociaciones
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.totalAssociations}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                <Building2 className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-white to-orange-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Clubes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.totalClubs}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">
                <Award className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de Asociaciones */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-500/25">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <CardTitle className="text-lg">Resumen por Asociación</CardTitle>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredStats.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Asociación
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Clubes
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Miembros
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Activos
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Tasa de Actividad
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStats.map((assoc) => (
                    <tr
                      key={assoc.id}
                      className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors duration-150"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {assoc.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {assoc.region || "Sin región"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className="bg-purple-100 text-purple-700">
                          {assoc.clubCount}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold text-gray-900">
                          {assoc.totalMembers}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-green-600 font-medium">
                          {assoc.activeMembers}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                assoc.activeRate >= 80
                                  ? "bg-green-500"
                                  : assoc.activeRate >= 50
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                              }`}
                              style={{ width: `${assoc.activeRate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 w-12">
                            {assoc.activeRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {selectedAssociationId === "all" && (
                  <tfoot>
                    <tr className="bg-gray-50 font-medium">
                      <td className="py-3 px-4 text-gray-900">Total General</td>
                      <td className="py-3 px-4 text-center text-gray-900">
                        {filteredStats.reduce((sum, a) => sum + a.clubCount, 0)}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-900">
                        {filteredStats.reduce(
                          (sum, a) => sum + a.totalMembers,
                          0,
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-green-600">
                        {filteredStats.reduce(
                          (sum, a) => sum + a.activeMembers,
                          0,
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-600">-</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <BarChart3 className="w-8 h-8 text-gray-500" />
              </div>
              <p className="font-medium">No hay datos disponibles</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nota informativa */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25 flex-shrink-0">
              <PieChart className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-800">
                <strong>Vista de Unión:</strong> Aquí puedes ver estadísticas
                agregadas de miembros por asociación. Los reportes financieros
                detallados están disponibles a nivel de club.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// VISTA DE REPORTES PARA ASOCIACIÓN (clubes propios)
// ============================================
function AssociationReportsView() {
  const { profile } = useAuthStore();
  const associationName = profile?.scopeName ?? "Mi Asociación";
  const { data: members, isLoading: loadingMembers } = useScopedMembers();
  const { data: clubs } = useAccessibleClubs();

  const stats = useMemo(() => {
    const list = members ?? [];
    const active = list.filter((m) => m.status === "active").length;
    return {
      totalMembers: list.length,
      activeMembers: active,
      totalClubs: clubs?.length ?? 0,
      activeRate:
        list.length > 0 ? Math.round((active / list.length) * 100) : 0,
    };
  }, [members, clubs]);

  const statsByClub = useMemo(() => {
    if (!members || !clubs) return [];
    return clubs
      .map((club) => {
        const clubMembers = members.filter((m) => m.clubId === club.id);
        const active = clubMembers.filter((m) => m.status === "active").length;
        return {
          id: club.id,
          name: club.name,
          totalMembers: clubMembers.length,
          activeMembers: active,
          activeRate:
            clubMembers.length > 0
              ? Math.round((active / clubMembers.length) * 100)
              : 0,
        };
      })
      .sort((a, b) => b.totalMembers - a.totalMembers);
  }, [members, clubs]);

  if (loadingMembers) {
    return (
      <div className="flex items-center justify-center h-64">
        <InlineLoader size="lg" label="Cargando reportes…" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            Reportes — {associationName}
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            Estadísticas por club de tu asociación
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-blue-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Total Miembros
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.totalMembers}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Users className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-green-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Miembros Activos
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.activeMembers}
                </p>
                <p className="text-xs text-green-600 font-medium mt-1">
                  {stats.activeRate}% del total
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-orange-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Clubes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.totalClubs}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Award className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-500/25">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <CardTitle className="text-lg">Resumen por Club</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {statsByClub.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Club
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Miembros
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Activos
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Tasa de Actividad
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {statsByClub.map((club) => (
                    <tr
                      key={club.id}
                      className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors duration-150"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                            <Award className="w-5 h-5 text-white" />
                          </div>
                          <p className="font-medium text-gray-900">
                            {club.name}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center font-semibold text-gray-900">
                        {club.totalMembers}
                      </td>
                      <td className="py-3 px-4 text-center text-green-600 font-medium">
                        {club.activeMembers}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${club.activeRate >= 80 ? "bg-green-500" : club.activeRate >= 50 ? "bg-yellow-500" : "bg-red-500"}`}
                              style={{ width: `${club.activeRate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 w-12">
                            {club.activeRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-medium">
                    <td className="py-3 px-4 text-gray-900">Total General</td>
                    <td className="py-3 px-4 text-center text-gray-900">
                      {stats.totalMembers}
                    </td>
                    <td className="py-3 px-4 text-center text-green-600">
                      {stats.activeMembers}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">
                      {stats.activeRate}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Building2 className="w-8 h-8 text-gray-500" />
              </div>
              <p className="font-medium text-gray-700">
                Aún no hay clubes en tu asociación
              </p>
              <p className="text-sm mt-1 text-gray-500">
                Crea o aprueba clubes desde <strong>Organización</strong> para
                empezar a ver reportes.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// VISTA DE REPORTES PARA CLUB (Detallada)
// ============================================
function ClubReportsView() {
  const [activeReport, setActiveReport] = useState<ReportType>("overview");

  // Data - usando scope del usuario
  const { profile } = useAuthStore();
  const { isClubDirector } = usePermissions();
  const { data: clubs, isLoading: loadingClubs } = useAccessibleClubs();
  const { data: activePeriod } = useActivePeriod();

  // Usar el club del director o el primero disponible
  const defaultClub =
    isClubDirector && profile?.directorOfClub
      ? profile.directorOfClub
      : clubs?.[0];

  const { data: members } = useMembersByClub(defaultClub?.id);
  const { data: charges } = useChargesByClubAndPeriod(
    defaultClub?.id,
    activePeriod?.id,
  );
  const { data: payments } = usePaymentsByClub(defaultClub?.id);
  const { data: expenses } = useExpensesByClub(defaultClub?.id);
  const { data: financialSummary } = useFinancialSummary(
    defaultClub?.id,
    activePeriod?.id,
  );
  const { data: meetings } = useMeetingsByClubAndPeriod(
    defaultClub?.id,
    activePeriod?.id,
  );
  const { data: achievements } = useAchievementsByClub(
    defaultClub?.id || "",
    activePeriod?.id,
  );
  const { data: classProgress } = useClassProgressByClub(
    defaultClub?.id || "",
    activePeriod?.id,
  );

  // Estadísticas de miembros
  const memberStats = useMemo(() => {
    if (!members)
      return {
        total: 0,
        active: 0,
        inactive: 0,
        byPosition: {} as Record<string, number>,
      };

    const active = members.filter((m) => m.status === "active");
    const inactive = members.filter((m) => m.status !== "active");

    return {
      total: members.length,
      active: active.length,
      inactive: inactive.length,
      byPosition: active.reduce(
        (acc, m) => {
          const position = m.position || "Sin cargo";
          acc[position] = (acc[position] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
    };
  }, [members]);

  // Estadísticas financieras
  const financeStats = useMemo(() => {
    if (!charges || !payments || !expenses) {
      return {
        totalCharges: 0,
        pendingCharges: 0,
        paidCharges: 0,
        overdueCharges: 0,
        collectionRate: 0,
        totalPayments: 0,
        totalExpenses: 0,
        netBalance: 0,
        byPaymentMethod: {} as Record<string, number>,
        byExpenseCategory: {} as Record<string, number>,
      };
    }

    const now = new Date();
    const pending = charges.filter(
      (c) => c.status === "pending" || c.status === "partial",
    );
    const paid = charges.filter((c) => c.status === "paid");
    const overdue = pending.filter(
      (c) => c.dueDate && c.dueDate.toDate() < now,
    );

    const validPayments = payments.filter((p) => p.status === "valid");
    const validExpenses = expenses.filter((e) => e.status === "valid");

    const byMethod = validPayments.reduce(
      (acc, p) => {
        acc[p.paymentMethod] = (acc[p.paymentMethod] || 0) + p.amount;
        return acc;
      },
      {} as Record<string, number>,
    );

    const byCategory = validExpenses.reduce(
      (acc, e) => {
        acc[e.category] = (acc[e.category] || 0) + e.amount;
        return acc;
      },
      {} as Record<string, number>,
    );

    return {
      totalCharges: charges.length,
      pendingCharges: pending.length,
      paidCharges: paid.length,
      overdueCharges: overdue.length,
      collectionRate:
        charges.length > 0
          ? Math.round((paid.length / charges.length) * 100)
          : 0,
      totalPayments: financialSummary?.totalPaid || 0,
      totalExpenses: financialSummary?.totalExpenses || 0,
      netBalance: financialSummary?.netBalance || 0,
      byPaymentMethod: byMethod,
      byExpenseCategory: byCategory,
    };
  }, [charges, payments, expenses, financialSummary]);

  // Estadísticas de asistencia
  const attendanceStats = useMemo(() => {
    if (!meetings)
      return {
        total: 0,
        closed: 0,
        open: 0,
        avgAttendance: 0,
        totalAttendees: 0,
      };

    const closed = meetings.filter((m) => m.isClosed);
    const totalAttendance = closed.reduce(
      (sum, m) => sum + (m.attendanceCount || 0),
      0,
    );
    const totalMembers = closed.reduce(
      (sum, m) => sum + (m.totalMembers || 0),
      0,
    );

    return {
      total: meetings.length,
      closed: closed.length,
      open: meetings.filter((m) => !m.isClosed).length,
      avgAttendance:
        totalMembers > 0
          ? Math.round((totalAttendance / totalMembers) * 100)
          : 0,
      totalAttendees: totalAttendance,
    };
  }, [meetings]);

  // Estadísticas de progreso
  const progressStats = useMemo(() => {
    if (!achievements || !classProgress) {
      return {
        totalAchievements: 0,
        activeClasses: 0,
        completedClasses: 0,
        byType: {} as Record<string, number>,
      };
    }

    const byType = achievements.reduce(
      (acc, a) => {
        acc[a.type] = (acc[a.type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return {
      totalAchievements: achievements.length,
      activeClasses: classProgress.filter((c) => c.status === "in_progress")
        .length,
      completedClasses: classProgress.filter(
        (c) => c.status === "completed" || c.status === "invested",
      ).length,
      byType,
    };
  }, [achievements, classProgress]);

  const reportTabs = [
    { id: "overview", label: "Resumen", icon: BarChart3 },
    { id: "members", label: "Miembros", icon: Users },
    { id: "finance", label: "Finanzas", icon: DollarSign },
    { id: "attendance", label: "Asistencia", icon: ClipboardCheck },
    { id: "progress", label: "Progreso", icon: Award },
  ];

  const handleExportCSV = (type: string) => {
    switch (type) {
      case "members":
        exportToXlsx({
          filename: "miembros",
          sheetName: "Miembros",
          rows: members || [],
          columns: [
            {
              header: "Nombre",
              key: (m) =>
                m.person.fullName ||
                `${m.person.firstName ?? ""} ${m.person.lastName ?? ""}`.trim(),
              width: 35,
            },
            { header: "Estado", key: (m) => m.status, width: 14 },
            { header: "Cargo", key: (m) => m.position || "", width: 20 },
          ],
        });
        return;
      case "charges":
        exportToXlsx({
          filename: "cobros",
          sheetName: "Cobros",
          rows: charges || [],
          columns: [
            { header: "Concepto", key: (c) => c.conceptName, width: 30 },
            { header: "Miembro", key: (c) => c.personName, width: 30 },
            { header: "Monto", key: (c) => c.amount, width: 14 },
            { header: "Pagado", key: (c) => c.amountPaid, width: 14 },
            { header: "Pendiente", key: (c) => c.balance, width: 14 },
            { header: "Estado", key: (c) => c.status, width: 14 },
          ],
        });
        return;
    }
  };

  if (loadingClubs) {
    return (
      <div className="flex items-center justify-center h-64">
        <InlineLoader size="lg" label="Cargando reportes…" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Moderno */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            Reportes
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            {defaultClub
              ? `Estadísticas de ${defaultClub.name}`
              : "Selecciona un club"}
          </p>
        </div>
        {activePeriod && (
          <Badge variant="info" className="px-3 py-1.5">
            Periodo: {activePeriod.name}
          </Badge>
        )}
      </div>

      {/* Report Tabs Modernos */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {reportTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveReport(tab.id as ReportType)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
              activeReport === tab.id
                ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30"
                : "bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-300"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Report Content */}
      {activeReport === "overview" && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="py-5 text-center">
                <div className="w-14 h-14 mx-auto bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300 mb-3">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {memberStats.active}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Miembros Activos
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="py-5 text-center">
                <div className="w-14 h-14 mx-auto bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300 mb-3">
                  <DollarSign className="w-7 h-7 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(financeStats.netBalance)}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Balance Neto
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="py-5 text-center">
                <div className="w-14 h-14 mx-auto bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300 mb-3">
                  <ClipboardCheck className="w-7 h-7 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {attendanceStats.avgAttendance}%
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Asistencia Promedio
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="py-5 text-center">
                <div className="w-14 h-14 mx-auto bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform duration-300 mb-3">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {progressStats.totalAchievements}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Especialidades Registradas
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-500/25">
                    <PieChart className="w-5 h-5 text-white" />
                  </div>
                  Distribución por Cargo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(memberStats.byPosition)
                    .slice(0, 5)
                    .map(([position, count]) => (
                      <div
                        key={position}
                        className="flex justify-between items-center"
                      >
                        <span className="text-gray-600">{position}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{
                                width: `${memberStats.active > 0 ? (count / memberStats.active) * 100 : 0}%`,
                              }}
                            />
                          </div>
                          <span className="font-medium w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  {Object.keys(memberStats.byPosition).length === 0 && (
                    <p className="text-gray-500 text-center py-4">
                      No hay datos de cargos
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-md shadow-green-500/25">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  Resumen Financiero
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Recaudado</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(financeStats.totalPayments)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gastado</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(financeStats.totalExpenses)}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-medium text-gray-700">Balance</span>
                    <span
                      className={`font-bold ${financeStats.netBalance >= 0 ? "text-green-600" : "text-red-600"}`}
                    >
                      {formatCurrency(financeStats.netBalance)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeReport === "members" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Can permission="reports.export">
              <Button size="sm" onClick={() => handleExportCSV("members")}>
                <Download className="w-4 h-4 mr-2" />
                Exportar Excel
              </Button>
            </Can>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-blue-600">
                  {memberStats.total}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Total Registrados
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-green-600">
                  {memberStats.active}
                </p>
                <p className="text-sm font-medium text-gray-500">Activos</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-gray-500">
                  {memberStats.inactive}
                </p>
                <p className="text-sm font-medium text-gray-500">Inactivos</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-purple-600">
                  {memberStats.active > 0
                    ? Math.round((memberStats.active / memberStats.total) * 100)
                    : 0}
                  %
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Tasa de Retención
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Distribución Detallada por Cargo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(memberStats.byPosition).map(
                  ([position, count]) => (
                    <div
                      key={position}
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200"
                    >
                      <span className="font-medium text-gray-700">
                        {position}
                      </span>
                      <Badge className="bg-primary-100 text-primary-700">
                        {count}
                      </Badge>
                    </div>
                  ),
                )}
                {Object.keys(memberStats.byPosition).length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    No hay datos de cargos
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeReport === "finance" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Can anyOf={["reports.export", "finance.export"]}>
              <Button size="sm" onClick={() => handleExportCSV("charges")}>
                <Download className="w-4 h-4 mr-2" />
                Exportar Cobros
              </Button>
            </Can>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(financeStats.totalPayments)}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Total Recaudado
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-red-600">
                  {formatCurrency(financeStats.totalExpenses)}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Total Gastos
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-yellow-600">
                  {financeStats.pendingCharges}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Cobros Pendientes
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-blue-600">
                  {financeStats.collectionRate}%
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Tasa de Cobro
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Pagos por Método</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(financeStats.byPaymentMethod).map(
                    ([method, amount]) => (
                      <div
                        key={method}
                        className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50/50 to-white rounded-xl border border-green-100 hover:border-green-200 hover:shadow-sm transition-all duration-200"
                      >
                        <span className="capitalize font-medium text-gray-700">
                          {method}
                        </span>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(amount)}
                        </span>
                      </div>
                    ),
                  )}
                  {Object.keys(financeStats.byPaymentMethod).length === 0 && (
                    <p className="text-gray-500 text-center py-4">
                      No hay pagos registrados
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Gastos por Categoría</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(financeStats.byExpenseCategory)
                    .slice(0, 5)
                    .map(([category, amount]) => (
                      <div
                        key={category}
                        className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50/50 to-white rounded-xl border border-red-100 hover:border-red-200 hover:shadow-sm transition-all duration-200"
                      >
                        <span className="capitalize font-medium text-gray-700">
                          {category}
                        </span>
                        <span className="font-semibold text-red-600">
                          {formatCurrency(amount)}
                        </span>
                      </div>
                    ))}
                  {Object.keys(financeStats.byExpenseCategory).length === 0 && (
                    <p className="text-gray-500 text-center py-4">
                      No hay gastos registrados
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeReport === "attendance" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-blue-600">
                  {attendanceStats.total}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Total Reuniones
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-green-600">
                  {attendanceStats.closed}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Con Asistencia
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-purple-600">
                  {attendanceStats.avgAttendance}%
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Asistencia Promedio
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-yellow-600">
                  {attendanceStats.totalAttendees}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Total Asistencias
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Resumen de Reuniones</CardTitle>
            </CardHeader>
            <CardContent>
              {meetings && meetings.length > 0 ? (
                <div className="space-y-2">
                  {meetings.slice(0, 10).map((meeting) => (
                    <div
                      key={meeting.id}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200"
                    >
                      <div>
                        <p className="font-medium">{meeting.title}</p>
                        <p className="text-sm text-gray-500">
                          {meeting.date.toDate().toLocaleDateString("es-CO")}
                        </p>
                      </div>
                      <div className="text-right">
                        {meeting.isClosed ? (
                          <>
                            <p className="font-bold">
                              {meeting.attendanceCount}/{meeting.totalMembers}
                            </p>
                            <p className="text-sm text-gray-500">
                              {meeting.totalMembers > 0
                                ? Math.round(
                                    (meeting.attendanceCount /
                                      meeting.totalMembers) *
                                      100,
                                  )
                                : 0}
                              %
                            </p>
                          </>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            Pendiente
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No hay reuniones registradas
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeReport === "progress" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-yellow-600">
                  {progressStats.totalAchievements}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Especialidades
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-blue-600">
                  {progressStats.activeClasses}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Clases en Progreso
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-green-600">
                  {progressStats.completedClasses}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Clases Completadas
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="py-4">
                <p className="text-3xl font-bold text-purple-600">
                  {progressStats.totalAchievements +
                    progressStats.completedClasses}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Total Logros
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Logros por Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(progressStats.byType).map(([type, count]) => (
                  <div
                    key={type}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50/50 to-white rounded-xl border border-yellow-100 hover:border-yellow-200 hover:shadow-sm transition-all duration-200"
                  >
                    <span className="capitalize font-medium text-gray-700">
                      {type}
                    </span>
                    <Badge className="bg-yellow-100 text-yellow-700">
                      {count}
                    </Badge>
                  </div>
                ))}
                {Object.keys(progressStats.byType).length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    No hay especialidades registradas
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
